import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { CloseIcon } from "../../assets/Icons";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { generateRandomUUID } from "../../utils/util.js";

const createEmptyConfig = () => ({
  enabled: false,
  heading: "",
  options: [{ id: generateRandomUUID(), label: "" }],
});

const QuestionSelectionListModal = ({ open, onClose, question, questionIndex, onSaved }) => {
  const { uuid: machineId } = useParams();
  const configData = useSelector((state) => state.configSlice);
  const userData = useSelector((state) => state.userSlice);
  const API_URL = configData.config.API_URL;

  const [selectionConfig, setSelectionConfig] = useState(createEmptyConfig);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !question) return;
    const existing = question.selectionConfig;
    setSelectionConfig({
      enabled: Boolean(existing?.enabled),
      heading: existing?.heading || "",
      options:
        Array.isArray(existing?.options) && existing.options.length
          ? existing.options.map((item) => ({
              id: item?.id || generateRandomUUID(),
              label: item?.label || "",
            }))
          : [{ id: generateRandomUUID(), label: "" }],
    });
    setError("");
  }, [open, question]);

  const updateOptionLabel = (id, value) => {
    setSelectionConfig((prev) => ({
      ...prev,
      options: prev.options.map((opt) => (opt.id === id ? { ...opt, label: value } : opt)),
    }));
    setError("");
  };

  const addOptionRow = () => {
    setSelectionConfig((prev) => ({
      ...prev,
      options: [...prev.options, { id: generateRandomUUID(), label: "" }],
    }));
  };

  const removeOptionRow = (id) => {
    setSelectionConfig((prev) => {
      const next = prev.options.filter((opt) => opt.id !== id);
      return {
        ...prev,
        options: next.length ? next : [{ id: generateRandomUUID(), label: "" }],
      };
    });
  };

  const handleSave = async () => {
    if (!question?.uuid) return;

    const filteredOptions = selectionConfig.options
      .map((item) => ({
        id: item.id || generateRandomUUID(),
        label: (item.label || "").trim(),
      }))
      .filter((item) => item.label.length > 0);
    const heading = (selectionConfig.heading || "").trim();

    if (selectionConfig.enabled && !heading) {
      setError("Popup heading is required when list is enabled.");
      return;
    }

    if (selectionConfig.enabled && filteredOptions.length === 0) {
      setError("Add at least one option when popup is enabled.");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/machine/questions/update/${question.uuid}`,
        {
          machineId,
          title: question.title,
          tags: (question.tags || []).map((t) => t.id),
          selectionConfig: {
            enabled: selectionConfig.enabled,
            heading,
            options: filteredOptions,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );
      onSaved?.();
      onClose();
    } catch (err) {
      console.log(err);
      setError("Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  if (!question) return null;

  return (
    <Modal show={open} size="md" centered onHide={handleClose} className="selection-list-modal">
      <Modal.Header className="selection-list-modal__header">
        <div className="selection-list-modal__header-content">
          <p className="selection-list-modal__eyebrow">Mobile popup options</p>
          <h5 className="selection-list-modal__title m-0">Configure selection list</h5>
          <p className="selection-list-modal__subtitle m-0">
            Question {questionIndex + 1}: {question.title}
          </p>
        </div>
        <button
          type="button"
          className="selection-list-modal__close"
          onClick={handleClose}
          aria-label="Close"
        >
          <CloseIcon />
        </button>
      </Modal.Header>
      <Modal.Body className="selection-list-modal__body">
        <div className="selection-list-toggle card-like">
          <div>
            <p className="mb-1 fw-semibold">Show popup on mobile after answer</p>
            <p className="mb-0 text-muted small">
              User picks one option (e.g. EPT1, EPT2) before moving to the next question.
            </p>
          </div>
          <label className="selection-switch">
            <input
              type="checkbox"
              checked={selectionConfig.enabled}
              onChange={(e) => {
                setSelectionConfig((prev) => ({ ...prev, enabled: e.target.checked }));
                setError("");
              }}
            />
            <span className="selection-switch__slider" />
          </label>
        </div>

        {selectionConfig.enabled && (
          <div className="selection-options-panel">
            <label className="selection-heading-field">
              <span className="fw-semibold">Popup heading</span>
              <span className="text-muted small d-block mb-1">
                Shown as the title on the mobile popup (required).
              </span>
              <input
                autoComplete="off"
                className="input form-control"
                type="text"
                placeholder="e.g. Select Your EPT"
                value={selectionConfig.heading}
                onChange={(e) => {
                  setSelectionConfig((prev) => ({ ...prev, heading: e.target.value }));
                  setError("");
                }}
              />
            </label>
            <div className="d-flex align-items-center justify-content-between mb-2 mt-3">
              <p className="mb-0 fw-semibold">List options</p>
              <button type="button" className="plain-btn" onClick={addOptionRow}>
                + Add option
              </button>
            </div>
            {selectionConfig.options.map((option, index) => (
              <div className="selection-option-row" key={option.id}>
                <span className="selection-option-row__index">{index + 1}</span>
                <input
                  autoComplete="off"
                  className="input form-control"
                  type="text"
                  placeholder={`e.g. EPT${index + 1}`}
                  value={option.label}
                  onChange={(e) => updateOptionLabel(option.id, e.target.value)}
                />
                <button
                  type="button"
                  className="selection-option-row__remove"
                  onClick={() => removeOptionRow(option.id)}
                  title="Remove option"
                  aria-label="Remove option"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {error ? <p className="error mt-3 mb-0">{error}</p> : null}
      </Modal.Body>
      <Modal.Footer className="footerbtn">
        <button className="cancel" type="button" onClick={handleClose} disabled={saving}>
          Cancel
        </button>
        {saving ? (
          <button className="loading save" type="button">
            <svg viewBox="25 25 50 50">
              <circle r="20" cy="50" cx="50" />
            </svg>
          </button>
        ) : (
          <button className="save" type="button" onClick={handleSave}>
            Save
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default QuestionSelectionListModal;
